export const KM_TO_MILES = 0.621371;
export const M_TO_FEET = 3.28084;
export const MM_TO_INCHES = 1 / 25.4;
export const CM_TO_INCHES = 1 / 2.54;
export const SQM_TO_ACRE = 1 / 4046.8564224;
export const SQM_TO_SQFT = 10.7639104;
export const SQM_TO_HA = 1 / 10000;
export const SQM_TO_SQKM = 1 / 1_000_000;

/**
 * Format a signed area in square metres to a human-readable string
 * respecting the user's unit preference. Auto-picks a unit that keeps
 * the number readable: sqft under an acre, then acres; m² under a
 * hectare, then hectares, then km² for really large draws. Trail
 * networks fit comfortably in the middle brackets.
 */
export function fmtArea(sqm, unit = 'metric') {
    if (sqm === null || sqm === undefined || !Number.isFinite(Number(sqm))) return null;
    const n = Math.abs(Number(sqm));
    if (unit === 'imperial') {
        const acres = n * SQM_TO_ACRE;
        if (acres < 1) return `${Math.round(n * SQM_TO_SQFT).toLocaleString()} ft²`;
        // 1-2 dp on acres so 0.8 → 0.8 acres and 12.3 → 12.3 acres both
        // read comfortably. Big draws still land at reasonable precision.
        return `${acres.toFixed(acres < 10 ? 2 : 1)} ac`;
    }
    if (n < 10_000) return `${Math.round(n).toLocaleString()} m²`;
    if (n < 1_000_000) return `${(n * SQM_TO_HA).toFixed(n < 100_000 ? 2 : 1)} ha`;
    return `${(n * SQM_TO_SQKM).toFixed(2)} km²`;
}

/**
 * Format a distance in km to a human-readable string respecting the
 * user's unit preference ('metric' | 'imperial').
 */
export function fmtDistance(km, unit = 'metric') {
    if (unit === 'imperial') {
        const miles = km * KM_TO_MILES;
        if (miles < 0.1) return `${Math.round(km * 1000 * M_TO_FEET)} ft`;
        return `${miles.toFixed(1)} mi`;
    }
    if (km < 0.1) return `${Math.round(km * 1000)} m`;
    return `${km.toFixed(1)} km`;
}

/**
 * Format an elevation in metres respecting the unit preference.
 * Used for elevation gain / loss / height-above-sea readouts.
 */
export function fmtElevation(m, unit = 'metric') {
    if (m === null || m === undefined) return null;
    if (unit === 'imperial') {
        return `${Math.round(Number(m) * M_TO_FEET)} ft`;
    }
    return `${Math.round(Number(m))} m`;
}

/**
 * Format a temperature in °C respecting the unit preference. Imperial
 * → whole °F; metric → one-decimal °C. Returns null for null input.
 */
export function fmtTemp(c, unit = 'metric') {
    if (c === null || c === undefined) return null;
    if (unit === 'imperial') return `${Math.round(Number(c) * 9 / 5 + 32)}°F`;
    return `${Number(c).toFixed(1)}°C`;
}

/**
 * Rounded temperature value (no unit suffix) for compact readouts like
 * the forecast strip's "max° / min°".
 */
export function tempValue(c, unit = 'metric') {
    if (c === null || c === undefined) return null;
    return Math.round(unit === 'imperial' ? Number(c) * 9 / 5 + 32 : Number(c));
}

/**
 * Format a rainfall/precipitation amount in mm respecting the unit
 * preference. Imperial → inches (2 dp); metric → mm (1 dp).
 */
export function fmtRain(mm, unit = 'metric') {
    if (mm === null || mm === undefined) return null;
    if (unit === 'imperial') return `${(Number(mm) * MM_TO_INCHES).toFixed(2)} in`;
    return `${Number(mm).toFixed(1)} mm`;
}

/**
 * Format a snow depth/accumulation in cm respecting the unit preference.
 * Imperial → inches (1 dp); metric → cm (1 dp).
 */
export function fmtSnow(cm, unit = 'metric') {
    if (cm === null || cm === undefined) return null;
    if (unit === 'imperial') return `${(Number(cm) * CM_TO_INCHES).toFixed(1)} in`;
    return `${Number(cm).toFixed(1)} cm`;
}

/**
 * Format a wind speed in km/h respecting the unit preference. Imperial
 * → mph; metric → km/h. Both rounded to a whole number.
 */
export function fmtSpeed(kmh, unit = 'metric') {
    if (kmh === null || kmh === undefined) return null;
    if (unit === 'imperial') return `${Math.round(Number(kmh) * KM_TO_MILES)} mph`;
    return `${Math.round(Number(kmh))} km/h`;
}

/** Rounded wind-speed value (no unit suffix) for compact "X / Y mph" readouts. */
export function speedValue(kmh, unit = 'metric') {
    if (kmh === null || kmh === undefined) return null;
    return Math.round(unit === 'imperial' ? Number(kmh) * KM_TO_MILES : Number(kmh));
}

/** Unit suffix for the active system: 'mph' | 'km/h'. */
export function speedUnit(unit = 'metric') {
    return unit === 'imperial' ? 'mph' : 'km/h';
}

// ── Explicit single-unit conversion ───────────────────────────────
//
// fmtDistance / fmtArea above auto-pick a unit and round for
// readability, which is right for a live readout but hides the number
// an operator sometimes wants: "0.5 km" when they need "500 m", or a
// metric org that wants the same draw in feet. These let a caller name
// the unit and get the exact converted value, independent of the org's
// default system.

/** Distance units, in the order a picker should offer them. */
export const DISTANCE_UNITS = [
    { key: 'm',  label: 'm',  system: 'metric',   from: (m) => m },
    { key: 'km', label: 'km', system: 'metric',   from: (m) => m / 1000 },
    { key: 'ft', label: 'ft', system: 'imperial', from: (m) => m * M_TO_FEET },
    { key: 'yd', label: 'yd', system: 'imperial', from: (m) => (m * M_TO_FEET) / 3 },
    { key: 'mi', label: 'mi', system: 'imperial', from: (m) => (m / 1000) * KM_TO_MILES },
];

/** Area units, in the order a picker should offer them. */
export const AREA_UNITS = [
    { key: 'm2',  label: 'm²',  system: 'metric',   from: (s) => s },
    { key: 'ha',  label: 'ha',  system: 'metric',   from: (s) => s * SQM_TO_HA },
    { key: 'km2', label: 'km²', system: 'metric',   from: (s) => s * SQM_TO_SQKM },
    { key: 'ft2', label: 'ft²', system: 'imperial', from: (s) => s * SQM_TO_SQFT },
    { key: 'ac',  label: 'ac',  system: 'imperial', from: (s) => s * SQM_TO_ACRE },
];

/**
 * Render a converted value at a precision that suits its magnitude,
 * then drop trailing zeros so 0.500 reads as 0.5 and 500.000 as 500.
 *
 * Scaling the decimals by magnitude is what makes one formatter work
 * across the whole set: the same draw is "500 m" (0 dp is plenty) and
 * "0.5 km" (needs 3 dp to not collapse to "1"). Thousands separators
 * only above 1000, where they aid reading rather than clutter.
 */
export function fmtUnitValue(value) {
    if (value === null || value === undefined || !Number.isFinite(Number(value))) return null;
    const v = Number(value);
    const a = Math.abs(v);
    let dp;
    if (a >= 1000) dp = 0;
    else if (a >= 100) dp = 1;
    else if (a >= 10) dp = 2;
    else dp = 3;
    let s = v.toFixed(dp);
    if (s.includes('.')) s = s.replace(/0+$/, '').replace(/\.$/, '');
    const [int, frac] = s.split('.');
    const grouped = Math.abs(Number(int)) >= 1000 ? Number(int).toLocaleString() : int;
    return frac ? `${grouped}.${frac}` : grouped;
}

/** Format metres in a named DISTANCE_UNITS key, e.g. ('m', 500) → "500 m". */
export function fmtDistanceIn(metres, unitKey) {
    const u = DISTANCE_UNITS.find((x) => x.key === unitKey);
    if (!u || metres === null || metres === undefined) return null;
    const v = fmtUnitValue(u.from(Number(metres)));
    return v === null ? null : `${v} ${u.label}`;
}

/** Format square metres in a named AREA_UNITS key. */
export function fmtAreaIn(sqm, unitKey) {
    const u = AREA_UNITS.find((x) => x.key === unitKey);
    if (!u || sqm === null || sqm === undefined) return null;
    const v = fmtUnitValue(u.from(Number(sqm)));
    return v === null ? null : `${v} ${u.label}`;
}

/**
 * Trail length that the server measured in MILES (the HQ figures),
 * shown in the reader's unit system. The number and its label come
 * from the same `units`, so a converted figure can never carry "mi".
 */
export const lengthUnit = (unit = 'metric') => (unit === 'imperial' ? 'mi' : 'km');
export function fromMiles(miles, unit = 'metric') {
    if (miles === null || miles === undefined || miles === '') return miles;
    return unit === 'imperial' ? Number(miles) : Number(miles) / KM_TO_MILES;
}
